import { Injectable } from "@angular/core";
import { doc, Firestore, setDoc } from "@angular/fire/firestore";
import { CurrencyType, IForcedSettings, ITournamentState, TableType } from "@soposopo/sopo-models";
import { firstValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ShopHomeService } from "src/app/shop/pages/shop-home/shop-home.service";
import { AuthStoreDispatcher } from "../state/auth/auth.dispatcher";
import { GameThemeSettingsDispatcher } from "../state/gameThemeSettings/gameThemeSettings.dispatcher";
import { Background, BetPresets, BetSizes, Item, RaiseSizes, ThemeOptions } from "../state/gameThemeSettings/gameThemeSettings.state";
import { ProfileStateDispatcher } from "../state/profile/profile.dispatcher";
import { ClubService, ItemTypes } from "./club.service";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { SopoConfirmSettings } from "src/app/sopo-popup/interfaces/confirm";
import { SopoCheatService } from "src/app/sopo-cheat.service";

export type BrockenImageSize = 'large' | 'medium' | 'small' | 'x-small' | 'xx-small';
export type ImageType = 'default' | 'avatar'; 

@Injectable({
    providedIn: 'root'
})
export class GameSettingsService {
    public static readonly avatarsWithBackgroundIds = ['79', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '100', '101','102', '103', '104', '105', '106', '107', '108', '109', '110', '111', '112', '113', '114', '115', '116', '117', '118', '119', '120', '121', '122', '123', '124', '125', '126', '127', '128', '129', '130', '131', '132', '133', '134', '135', '136', '137', '138', '139', '141', '142', '143', '144', '145', '146', '147', '148', '149', '150', '151', '152', '153', '154', '155', '156', '159', '160', '161', '162', '163', '164', '165', '166', '167', '168', '169', '170', '171', '172', '173', '174', '175', '176', '177', '178'];
    private static readonly currencyImages = {
        [CurrencyType.CHIP]: '/assets/icon/chips/redchip.png',
        [CurrencyType.SOPO_POINT]: 'assets/icon/sopo-diamond-logo.webp',
        [CurrencyType.GOLD]: '/assets/icon/chips/gold_chip.webp',
        [CurrencyType.SWEEPBUCK]: '/assets/img/sweep/money.webp',
        [CurrencyType.NONE]: '/assets/img/subscription/logoes/trophy_gold.webp',
    }
    private nftPopUpOpen = false;

    constructor(
        private clubService: ClubService,
        private profileService: ProfileStateDispatcher,
        private gameThemeSettingsDispatcher: GameThemeSettingsDispatcher,
        private shopHomeService: ShopHomeService,
        private firestore: Firestore,
        private authDispatcher: AuthStoreDispatcher,
        private sopoPopupService: SopoPopupService,
        private sopoCheatService: SopoCheatService,
        ) {}

    public async changeSetting(item: Item, pluralType: keyof ItemTypes) {
        const uid  = await this.profileService.getMyID();
        if (uid == null) {
            this.sopoPopupService.showAlert('app', {
                header: `Could not save settings`,
                subHeader: `Please try logging out and logging back in to try again.`,
                message: `The apllication could not process your request because profile is null`,
            });
        } else {
            if (pluralType === 'avatars') return this.clubService.submitSettingForItem(item, pluralType).then(() => { //call through functions
                this.profileService.fetchProfile(uid);
            });
            else return this.changeSettingWithoutFunction(item, pluralType, uid).then(() => {
                this.profileService.fetchProfile(uid);
            });
        }
    }

    public static getAvatarUrl(avatarId: string | number): string {
        const stringValue = avatarId.toString();
        if (
            stringValue.includes('http') ||
            stringValue.includes('assets/img') ||
            stringValue.includes('assets/icon')
        ) return stringValue;
        return `assets/img/avatars/${avatarId}.png`;
    }

    public static getSopoBrokenImageUrl(size: BrockenImageSize = 'x-small', imageType: ImageType = 'default'): string {
        switch (imageType) {
            case 'avatar':
                return `assets/img/broken-images/broken-image_xx-small.webp`;
            case 'default':
                return `assets/img/broken-images/broken-image_${size}.webp`;
        }
    }

    public static getCurrencyImage(currency: CurrencyType): string {
        if (currency) return this.currencyImages[currency];
        return this.currencyImages[CurrencyType.GOLD];
    }

    public static getFeltId(id: string | number): string {
        return this.getStringId(id);
    }

    public static getBackgroundId(id: string | number): string {
        return this.getStringId(id);
    }

    private static getStringId(id: string | number): string {
        if (typeof id === 'number') {
            return id.toString();
        }
        return '0';
    }

    mergeItems<T extends Item>(myItems: T[], allItems: T[]): T[] {
        const purchasedItems = myItems.map(item => ({
                ...item,
                purchased: true
            })
        );
        const filteredAllItems = allItems.filter(Item => myItems.find(myItem => myItem.id == Item.id) === undefined);
        const mergedItems: T[] = [...purchasedItems, ...filteredAllItems];
        mergedItems.sort((a, b) => a.id - b.id);
        return mergedItems;
    }

    getBackground(background: Background) {
        let backgroundId = '0';

        if (typeof background.id === 'number') {
            backgroundId = background.id.toString();
        }

        switch (background.type) {
            case 'image': 
                return {
                    'background-image': `url("/assets/img/backgrounds/${backgroundId}.png")`,
                    'background-size': 'cover',
                    'background-position': 'center'
                }
            case 'pattern': 
                return {
                    'background-image': `url("/assets/img/backgrounds/${backgroundId}.png")`,
                    'background-size': 'auto',
                    'background-repeat': 'repeat'
                }
            case 'color':
                return {
                    'background-color': background.value
                }
            case 'gradient':
                return {
                    'background': background.value
                }
            case 'main-stage':
                return {
                    'background-image': `url("/assets/img/backgrounds/main-stage-${backgroundId}.webp")`,
                    'background-size': 'cover',
                    'background-position': 'center'
                }
        }
    }

    async markItemAsPurchasedAndSelect<T extends Item> (item: T, itemType: keyof ItemTypes) {
        if (true) {
            this.changeSetting(item, itemType);
        } else {
            const userId  = await this.profileService.getMyID();
            
            const params = {
                bundle_id: item.bundleId,
                user_id: userId,
                price: item.price,
                [`${itemType.slice(0, -1)}_id`]: item.id,
                promo_code: '',
            };
            const result = await firstValueFrom(this.shopHomeService.purchaseItem(params, itemType));
            if (result.code === undefined) {
                this.getPurchasedItems(itemType);
                this.changeSetting(item, itemType);
            }
        }
    }

    changeSettingWithoutFunction<T extends Item> (item: T, itemType: keyof ItemTypes, userId: string) {
        const key = itemType.slice(0, -1);
        const docReference = doc(this.firestore, `users/${userId}/private/settings`);
        return setDoc(docReference, {
            [key]: item,
        }, { merge: true });
    }

    async selectAndPurchaseItem<T extends Item> (item: T, itemType: keyof ItemTypes) {
        if (item.purchased) {
            this.changeSetting(item, itemType)
        }
        if (!item.purchased && item.price !== undefined && item.price >= 0) {
            //purchase item
            const result = await this.clubService.purchaseItem(
                'diamond',
                {
                    type: itemType.slice(0, -1),
                    [itemType.slice(0, -1)]: item,
                },
                item.price,
            );
            if (result.code === undefined) { // the ourchase not resulted in an error
                this.getPurchasedItems(itemType);
                this.changeSetting(item, itemType) // set purchased item
            }
        }
    }

    getPurchasedItems(itemType: keyof ItemTypes) {
        switch (itemType) {
            case 'felts':
                this.gameThemeSettingsDispatcher.getPurchasedFelts();
                break;
            case 'backgrounds':
                this.gameThemeSettingsDispatcher.getPurchasedBackgrounds();
                break;
            case 'cardStyles':
                this.gameThemeSettingsDispatcher.getPurchasedCardStyles();
                break;
        }
    }

    getPrivateUserDataDocRef(Id: string) {
        return doc(this.firestore, `users/${Id}/private/settings`);
    }

    public async saveRaiseSizes(raiseSizes: RaiseSizes){
        const uid  = await this.profileService.getMyID();
        setDoc(this.getPrivateUserDataDocRef(uid), {raiseSizes}, {merge: true});
    }

    public async saveBetSizes(betSizes: BetSizes){
        const uid  = await this.profileService.getMyID();
        setDoc(this.getPrivateUserDataDocRef(uid), {betSizes}, {merge: true});
    }

    public async saveBetPresets(betPresets: BetPresets){
        const uid  = await this.profileService.getMyID();
        setDoc(this.getPrivateUserDataDocRef(uid), {betPresets}, {merge: true});
    }
    
    public async saveOptions(optionType: keyof ThemeOptions, optionValue: boolean){
        const uid  = await this.profileService.getMyID();
        setDoc(this.getPrivateUserDataDocRef(uid), {[optionType]: optionValue}, {merge: true});
    }

    async loadAllItemsFromCms<T extends Item> (itemType: keyof ItemTypes): Promise<T[]> {
        try {
            const response = await firstValueFrom(this.shopHomeService.getData(itemType));
            let itemsArray: T[] = [];
            response.bundles.forEach(bundle => {
                itemsArray.push(...(bundle[itemType].map(item => {
                    return {...item, bundleId: bundle.id};
                })));
            });
            return itemsArray;
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    async showNftThemeSettingsAlertIfNeeded(isInProfilePage: boolean) {
        if(this.nftPopUpOpen) return;
        this.nftPopUpOpen = true;
        const myId = await this.authDispatcher.getMyID();
        const itemKey = `dontShowAgain_themeSettingsAlert_${myId}`;
        const dontShow = localStorage.getItem(itemKey);
        if (dontShow) return;
        const usingCroppedImage = await firstValueFrom(this.profileService.getProfileState()
        .pipe(
            map((profileState) => {
                if (!profileState.profileData || !profileState.profileData.nft) return null
                return profileState.profileData.nft.usingCroppedImage
            }),
            filter((usingCroppedImage) => usingCroppedImage !== null)
        ));

        const avatarsInCircle = await firstValueFrom(this.gameThemeSettingsDispatcher.getThemeOptions('avatarsInCircle'));

        const warning = "NFTs with backgrounds more complicated than solid colors may experience errors in trimming of backgrounds. We apologize in advance for any inconveniences, and are working on the software`s accuracy in the meantime.";
        const croppedNftBody = `You have chosen to remove the background from your NFT but in "Theme Settings" "Show avatars in circle" is turned on. Do you want to turn it off?`
        const notCroppedNftBody = 'You have chosen to use NFT as an avatar, but in "Theme Settings," "Show avatars in circle is turned off. Do you want to turn it on? You may change this later.'
        if ((avatarsInCircle === false && !usingCroppedImage) || (avatarsInCircle === true && usingCroppedImage)) {
            const dialogOptions: SopoConfirmSettings = {
                zIndex: !isInProfilePage ? 2002 : 0,
                data: {
                    header: 'Theme settings',
                    warning: warning,
                    body: `${usingCroppedImage ? croppedNftBody : notCroppedNftBody} You can change this later from table menu under "Theme Settings".`,
                    dontShowAgain: 'themeSettingsAlert',
                    cancelText: 'No',
                    okText: 'Yes',
                }
            };
            this.sopoPopupService.promptForConfirmation('app', dialogOptions).then(answer => {
                if (answer) this.gameThemeSettingsDispatcher.setThemeOptions('avatarsInCircle', usingCroppedImage ? false : true);
                this.nftPopUpOpen = false;
            })
        } else this.nftPopUpOpen = false;
    }

    public getCheatedSpinAndGoIndex(tournament: ITournamentState): number {
        //for jackpot's multiplier cheat
        let cheatedIndex = tournament?.spinAndGoIndex ?? -1;
        if (tournament.tournamentType === TableType.SNG && tournament?.settings?.isSpinAndGo && tournament?.spinAndGoIndex >= 0) {
            const multiplierCheat = this.sopoCheatService.jackpotMultiplier;

            if (multiplierCheat > -1) {
                cheatedIndex = multiplierCheat === 6 && tournament.currency !== CurrencyType.SWEEPBUCK ? tournament.spinAndGoIndex : multiplierCheat;
            }
        }
        return cheatedIndex;
    }

    public getForcedSettings(tournament: ITournamentState): IForcedSettings {
        const forcedSettings = { ...tournament.settings.forcedSettings };
        if (tournament.tournamentType === TableType.SNG && tournament?.settings?.isSpinAndGo && tournament?.spinAndGoIndex >= 0) {
            const index = this.getCheatedSpinAndGoIndex(tournament);
            switch(index) {
                case 0:
                    forcedSettings.felt = {
                        id: 5,
                        name: "Yellow",
                    }
                    break;
                case 1:
                    forcedSettings.felt = {
                        id: 3,
                        name: "Purple",
                    }
                    break;
                case 2:
                    forcedSettings.felt = {
                        id: 5,
                        name: "Yellow",
                    }
                    break;
                case 3:
                    forcedSettings.felt = {
                        id: 0,
                        name: "Blue",
                    }
                    break;
                case 4:
                    forcedSettings.felt = {
                        id: 4,
                        name: "Red",
                    }
                    break;
                case 5:
                    forcedSettings.felt = {
                        id: 4,
                        name: "Red",
                    }
                    break;
                case 6:
                    forcedSettings.felt = {
                        id: 1,
                        type: 'bitcoin',
                    }
                    break;
            }
        }

        return forcedSettings;
    }
}
